import { useInView } from '../../../hooks/useInView';

const features = [
  {
    icon: 'ri-focus-3-line',
    title: 'Deeply Personalized Approach',
    desc: 'No generic protocols — ever. Every session is uniquely crafted around your body, your history, and your goals.',
  },
  {
    icon: 'ri-search-eye-line',
    title: 'Root Cause, Not Surface Symptoms',
    desc: 'We address what\'s actually driving your discomfort or imbalance rather than simply managing what\'s visible on the surface.',
  },
  {
    icon: 'ri-moon-foggy-line',
    title: 'A Calm, Intentional, High-End Environment',
    desc: 'Carefully curated to signal safety — so your body and nervous system can fully settle into the healing work.',
  },
  {
    icon: 'ri-emotion-happy-line',
    title: 'Consistent Client Retention & Satisfaction',
    desc: 'Clients return not because they have to, but because the results they experience speak for themselves.',
  },
  {
    icon: 'ri-bar-chart-line',
    title: 'Designed for Long-Term Transformation',
    desc: 'Rather than chasing temporary relief, the goal is durable change that compounds and strengthens over time.',
  },
];

export default function WhyChooseUs() {
  const { ref, inView } = useInView();

  return (
    <section
      id="why-us"
      ref={ref as React.RefObject<HTMLElement>}
      className="bg-[#080808] py-32 border-t border-[#d4af37]/8"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: Image with decorative stack */}
          <div
            className={`relative transition-all duration-700 ${inView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
          >
            {/* Main image */}
            <div className="relative w-full h-[580px] rounded-2xl overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/b38b5e017d7cad259ff58081a5e9cee8.jpg"
                alt="Khalsa Healing practitioner performing therapy"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#080808]/60 via-transparent to-transparent" />
            </div>

            {/* Floating proof card */}
            <div className="absolute -bottom-6 -right-6 bg-[#0d0d0d] border border-[#d4af37]/20 rounded-xl p-6 w-52">
              <div className="flex items-center gap-1 mb-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#d4af37] text-xs" />
                ))}
              </div>
              <p className="text-white text-xs font-medium mb-1" style={{ fontFamily: 'Inter, sans-serif' }}>
                5-Star Experience
              </p>
              <p className="text-[#4a4a4a] text-xs" style={{ fontFamily: 'Inter, sans-serif' }}>
                Consistent results across 500+ sessions
              </p>
            </div>

            {/* Decorative frame */}
            <div className="absolute -top-4 -left-4 w-24 h-24 border border-[#d4af37]/10 rounded-xl pointer-events-none" />
          </div>

          {/* Right: Content */}
          <div className={`transition-all duration-700 ${inView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`} style={{ transitionDelay: '150ms' }}>
            <div className="flex items-center gap-4 mb-5">
              <span className="w-8 h-px bg-[#d4af37]/30" />
              <p className="text-[#d4af37] uppercase tracking-[0.3em] text-[10px]" style={{ fontFamily: 'Inter, sans-serif' }}>
                Why Choose Us
              </p>
            </div>
            <h2
              className="text-4xl md:text-5xl text-white font-light leading-tight mb-12"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Why Clients Choose<br />
              <em className="text-[#d4af37] not-italic">Khalsa Healing</em>
            </h2>

            <div className="space-y-8">
              {features.map((feat, idx) => (
                <div key={idx} className="flex items-start gap-5 group">
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 bg-[#0f0f0f] border border-[#1a1a1a] group-hover:border-[#d4af37]/20 rounded-xl transition-colors duration-300">
                    <i className={`${feat.icon} text-[#d4af37] text-base`} />
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium mb-1.5" style={{ fontFamily: 'Inter, sans-serif' }}>
                      {feat.title}
                    </p>
                    <p className="text-[#5a5a5a] text-sm leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                      {feat.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
